// Boot restructure (Checkpoint 5.01 — Phase 1a, Lane B). This is now the ONLY module index.html loads.
// The engine (main.js) is a flat module whose top-level await BOOTS the game the instant it is imported
// (5.00 discovery). So the auth gate lives UPSTREAM of that import: we never statically import main.js;
// we resolve auth first, and dynamic-import the engine only once a user is signed in. That guarantees no
// flash of game before auth resolves, and composes cleanly with the engine's existing course-load await
// (which only starts running after this import fires).
//
// Flow:
//   page load → #boot-init overlay is already visible (in index.html, zero-JS, no flash)
//   watchAuth → FIRST callback = "initializing resolved"
//     signed in  → hide init, import('./main.js') ONCE (the game boots as it does today), mount sign-out
//     signed out → hide init, show the branded splash (sign-in required to reach the game)
//   live after: a sign-out fires the callback with null → reload to a fresh splash (the running engine
//   can't be un-booted, so a clean reload is the honest reset); a sign-in from the splash fires it with a
//   user → the same booted-guard imports the game exactly once.
import './firebase.js';               // side-effect: initializeApp + getAuth (one instance, imported everywhere)
import { watchAuth, doSignOut } from './auth.js';
import { showSplash, hideSplash } from './splash.js';

let booted = false;                   // main.js imported? (import() caches, but guard the side-effects too)
let inCatalogue = false;              // the catalogue overlay is up (pre-game)
let currentUser = null;
let catalogueMod = null;              // the DYNAMICALLY-imported catalogue module
let audioMod = null;                  // the DYNAMICALLY-imported audio module (5.36 — menu music over the catalogue)
// 6.0 — THE GAME ID, not an edition slug. Editions died with the tour, and the progress document had to be
// re-keyed anyway, so it was re-keyed to something game-scoped rather than golf-shaped: `progress/{gameId}`.
// `golf` is the only value today and may be the only value ever — the point is that the KEY does not have to
// change if that stops being true, and a persisted key is the one thing that is expensive to change later.
const GAME_ID = 'golf';

const initEl = document.getElementById('boot-init');
const hideInit = () => { if (initEl) initEl.style.display = 'none'; };
const showInit = () => { if (initEl) initEl.style.display = 'flex'; };

// 5.87 — dress the loading cover with the same PrettyGolf branding as the menu: the splashbg.jpg hero behind it
// (the scrim lives in the #boot-init::before CSS) and the wordmark centred over the horizontal loader. Both assets
// are Vite-hashed, so their URLs must be resolved here — static CSS/HTML can't template them. Runs at module load,
// before auth, so the brand lands essentially immediately; a resolve failure just leaves the #10160f base + loader
// (never a broken frame). Same assets the splash + catalogue use → menu→loading→play is one branded space.
(function dressBootInit() {
  if (!initEl) return;
  try {
    const bg = Object.values(import.meta.glob('../static/brand/splashbg.jpg', { eager: true, query: '?url', import: 'default' }))[0];
    if (bg) initEl.style.backgroundImage = `url("${bg}")`;
  } catch (e) { console.warn('[boot] splashbg failed to resolve — plain dark cover:', e); }
  try {
    const logo = Object.values(import.meta.glob('../static/brand/logo-PrettyGolf1.png', { eager: true, query: '?url', import: 'default' }))[0];
    const img = initEl.querySelector('.bi-logo');
    if (img && logo) img.src = logo;
  } catch (e) { console.warn('[boot] PrettyGolf logo failed to resolve — loader only:', e); }
})();

async function bootGame(user) {
  if (booted) return;
  booted = true;
  hideSplash();
  showInit();                         // keep the spinner up as the "loading" cover through the engine load
  // First persistence slice (Checkpoint 5.05 — Phase 1b-i): provision the user's Firestore state (create the
  // user doc on first sign-in + seed the game's progress doc). DYNAMIC import so the heavy Firestore SDK
  // loads only here, on the first authed boot — never on the splash/auth path. Deliberately NOT awaited: it
  // runs in parallel with the engine load and must NEVER block or fail the game (a Firestore hiccup just
  // means the doc is (re)created next sign-in — provisionUser is idempotent).
  import('./db.js')
    .then((db) => db.provisionUser(user))
    .catch((e) => console.warn('[db] user provisioning failed (will retry next sign-in):', e));
  await import('./main.js');          // the engine boots here — unchanged, exactly as before
  hideInit();                         // reveal the fully-loaded game (no black gap while three.js loads)
  mountHomeControl();                 // the in-game exit-to-menu house icon (5.42)
}

// The COURSE CATALOGUE (Checkpoint 5.14 as the World Tour; the catalogue since 6.0) sits BETWEEN auth-success
// and game boot: after auth we show the overlay INSTEAD of importing main.js, and the engine boots only when
// the player picks a course (launchCourse → bootGame). We must await provisioning here (unlike bootGame's
// fire-and-forget) so progress is seeded before the ONE cheap read the catalogue renders from; a hiccup →
// null progress, which the catalogue renders as the seed default (course 1 unlocked, rest locked).
async function enterCatalogue(user) {
  if (booted || inCatalogue) return;
  inCatalogue = true;
  currentUser = user;
  hideSplash();
  showInit();                         // spinner while db + progress load
  // DYNAMIC imports (authed-only): db.js pulls the Firestore SDK. Neither must weigh down the signed-out
  // splash — same discipline as the game's engine load. (The globe went with the tour at 6.0, so the
  // catalogue no longer drags THREE.js onto this path at all.)
  const [db, catalogue, audio] = await Promise.all([import('./db.js'), import('./catalogue/catalogue.js'), import('./audio.js')]);
  catalogueMod = catalogue;
  audioMod = audio;
  // Menu music (5.36). The graph is built HERE, not in main.js, because the catalogue is reached long before the
  // engine — and initAudio() is idempotent, so the engine's own call later lands on this same context (a
  // course launch does not reload the page). startMenuMusic() fetches + decodes 0.9 MB asynchronously and is
  // deliberately NOT awaited: the cards and a course launch stay immediate, and the loop arrives
  // when it arrives. If the context is suspended (a returning player has made no gesture — see audio.js), it
  // does not start(); the 5.29 gesture listeners start it on the first tap that resumes the context.
  audio.initAudio();
  audio.startMenuMusic();
  // 5.90 — provisionUser now RETURNS the user doc it already had to fetch. The Profile section prefills its
  // name fields from it, so this is a free read rather than a new one. A failure leaves userDoc null and the
  // Profile form simply starts empty (the same posture as a null progress doc rendering the seed default).
  let userDoc = null;
  try { userDoc = await db.provisionUser(user); } catch (e) { console.warn('[db] provisioning failed (catalogue renders seed default):', e); }
  let progress = null;
  try { progress = await db.getGameProgress(user.uid, GAME_ID); }
  catch (e) { console.warn('[catalogue] progress read failed — rendering seed default:', e); }
  // 5.90 — `onSignOut` rides in because the account badge is GONE: Profile is now the single identity home on
  // this screen, and it owns the sign-out that badge used to hold. One identity surface, not two.
  await catalogue.showCatalogue({
    user, userDoc, progress, db, gameId: GAME_ID,
    onLaunch: launchCourse,
    onSignOut: () => doSignOut().catch(() => { location.href = location.pathname; }),
  });
  hideInit();
}

// Launch a course from the catalogue via the URL channel the engine already honours (5.13 Lane A): New round =
// ?course=SLUG (NO ?hole — that flips the engine into single-hole practice mode, which disables persistence);
// Resume = ?course=SLUG&resume=1 (the engine auto-runs rehydrateSavedRound). Then fire the deferred main.js
// import (bootGame) IN-SESSION — main.js reads location.search fresh at import, so no reload is needed.
//
// 6.0 — `&mode=free` IS GONE. It existed because the active nav section (Tour vs Free Play) was the round's
// mode, and mode decided whether the broadcast layer existed. Tour and Free Play are now ONE catalogue and the
// broadcast layer is archived, so there is no second mode for a round to be in — the parameter, the field, the
// `__{mode}` id segment and the rules' modeOk() all retire together (mine-INV §8).
function launchCourse(slug, opts = {}) {
  // ── ⚠️ TWO CHANGES AT 7.0.1, AND THE SECOND IS THE ONE THAT MATTERS ─────────────────────────────────────
  //
  // 1. SEEDED FROM THE CURRENT URL, not from nothing. This line used to be `new URLSearchParams()` — empty —
  //    so a click on a course card silently discarded every parameter the player arrived with. 7.0 shipped a
  //    playable hole nobody could reach because of it: `?play` died here, `main.js` read the rewritten URL,
  //    `PLAYING` came out false and the entire input block never ran. Carrying them forward means the next
  //    flag cannot die at the same place.
  //
  // 2. ⚠️ AND A CATALOGUE LAUNCH *MEANS PLAY*. Carrying `?play` forward would have fixed the symptom and left
  //    the design wrong: a player who taps a course card is asking to play golf, and that must not depend on
  //    a URL parameter they will never type. So it is set here, explicitly, whatever the URL said.
  //
  //    The inspection camera and orbit mode are untouched — they are reached by the direct viewer URL, which
  //    is how they have been reached since 6.1 and how every judgement in this project has been made. A
  //    viewer-from-catalogue path would be an explicit opt-out and is deliberately NOT built.
  const p = new URLSearchParams(location.search);
  p.set('course', slug);
  p.set('play', '1');
  if (opts.resume) p.set('resume', '1'); else p.delete('resume');
  history.replaceState(null, '', location.pathname + '?' + p.toString());
  inCatalogue = false;
  // 5.36: the menu loop breathes out (600 ms) while the engine loads; main.js's startAmbient() brings the
  // pine bed up when its 2.9 MB have landed and decoded, which is always well after this fade has finished.
  // Fired before hideCatalogue()/bootGame() so the fade runs UNDER the load rather than truncated by it.
  if (audioMod) audioMod.stopMenuMusic();
  if (catalogueMod) catalogueMod.hideCatalogue();
  bootGame(currentUser);
}

watchAuth((user) => {
  if (user) {
    // A course in the URL = a deep-link, or a mid-round refresh (the tour sets ?course on launch) → boot the
    // game directly. A clean URL = the normal entry → the catalogue. Save&exit / completion reload to a clean
    // URL (location.pathname), which lands here with no ?course and re-shows it with fresh progression.
    if (new URLSearchParams(location.search).has('course')) bootGame(user);
    else enterCatalogue(user);
  } else {
    // Signed out. `?course` is an IN-SESSION game-launch marker (set by launchCourse); it must NEVER survive
    // the auth boundary, or it routes the NEXT sign-in — a brand-new registration, or a different account —
    // straight into the game and SKIPS the catalogue (5.18 regression: sign out from in-game → the old
    // location.reload() kept ?course → the next sign-in bootGame'd instead of showing it). Strip it so every
    // fresh sign-in lands on the catalogue (fail toward the catalogue, never skip it).
    if (booted || inCatalogue) { location.href = location.pathname; return; }   // clean reload → splash, no ?course
    if (new URLSearchParams(location.search).has('course')) {
      history.replaceState(null, '', location.pathname);                    // drop a stale ?course before splash
    }
    hideInit();
    showSplash();
  }
});

// --- the in-game home icon — Checkpoint 5.42; the tour badge retired at 5.90 ---------
// 5.25 mounted ONE control in both contexts: a profile badge that, in game, opened a popover whose only
// in-round item was Sign out. 5.42 SPLIT them — a house in game, the badge on the tour. 5.90 finishes the
// job and DELETES the badge outright: the menu now has a real PROFILE SECTION in the nav rail, which owns
// identity and the sign-out the badge was holding. Two profile affordances on one screen is the duplication
// that quietly ships, so there is now exactly one — and it is the one with a name in it.
//
//   IN THE GAME  → #home-btn, a house. In play, tapping your own face to leave the course makes no sense.
//                  It carries no menu: the icon IS the button the menu existed to hold, so there is no
//                  in-game popover left to hit-test, and _hittest-check.mjs shrinks rather than grows.
//   ON THE MENU  → nothing here. The nav rail's Profile section (catalogue.js) is the identity home.
//
// It also makes a rule STRUCTURAL instead of remembered: log out lives on the menu page, and the only route
// to the menu page is to exit. "You cannot log out mid-round" stops being a thing anyone has to enforce.
//
// aria-label is "Exit to menu", NOT "Home". The destination is the menu, and a screen reader
// announcing "Home" on a control that discards the hole in hand is the honesty wall in miniature. The
// label, the modal's title and its primary button all say MENU — one noun for one place.
//
// `touch-action: none` is not decoration. It sits in a HUD corner above the surface players drag across to
// swing; #strikepoint was a 74×74 px live target 16 px below the swing bar (5.37), and that is the lesson.
const HOME_STYLE_ID = 'pg-home-style';
const HOME_CSS = `
/* The in-game home icon. 5.83 §2 — moved into the left instrument column so it sits immediately to the LEFT of
   the audio (#mute) toggle, the two chrome controls now a tidy pair. Left-edge anchor (the mute pill is nudged
   one control-width right in index.html to make room); aligned to the mute row (top + 232px), just under the
   compass. Clear of the HUD (top-left pills end higher), the minimap (top-right) and the swing surface. */
#home-btn{position:fixed;left:max(10px,env(safe-area-inset-left));
  top:calc(max(8px,env(safe-area-inset-top)) + 232px);z-index:12;width:36px;height:36px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;
  color:#10160f;background:rgba(255,214,92,.9);border:1px solid rgba(255,214,92,.9);
  box-shadow:0 2px 8px rgba(0,0,0,.45);opacity:.85;transition:opacity .15s ease;
  touch-action:none;-webkit-tap-highlight-color:transparent;}
#home-btn:hover{opacity:1;}
/* Inert while a stroke resolves: the ball is in flight, or the camera is filming it, or it is dropping into
   the cup. Not because a half-written stroke is possible (exit writes nothing) but because a modal must not
   steal focus mid-swing. pointer-events:none so it cannot be tapped even at the edges. */
#home-btn[disabled]{opacity:.3;cursor:default;pointer-events:none;}
#home-btn svg{width:19px;height:19px;display:block;pointer-events:none;}
`;

function ensureHomeStyle() {
  if (document.getElementById(HOME_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HOME_STYLE_ID;
  style.textContent = HOME_CSS;
  document.head.appendChild(style);
}

// THE IN-GAME CONTROL (Checkpoint 5.42). A house. It always raises the confirmation modal — mid-hole, at the
// scorecard, everywhere: this is a phone, an unfamiliar icon gets prodded to find out what it is, and a
// player who prods it must not be ripped out of their round for the crime of curiosity. Two taps of friction
// were removed with the profile menu; one deliberate tap is put back.
//
// The engine owns the modal (it alone knows the resume hole and whether a stroke is resolving), so this
// publishes intent through `window.__pgExitPrompt` — the same "expose for the shell" seam as __pgResumeRound
// and __pgClean. boot.js mounts this AFTER `await import('./main.js')`, so the hook is always present.
function mountHomeControl() {
  if (document.getElementById('home-btn')) return;
  ensureHomeStyle();
  const btn = document.createElement('button');
  btn.id = 'home-btn';
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Exit to menu');   // NOT "Home" — the destination is the menu
  btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" '
    + 'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M3 11 12 3l9 8"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/></svg>';
  btn.addEventListener('click', () => { if (window.__pgExitPrompt) window.__pgExitPrompt(); });
  document.body.appendChild(btn);

  // Keep it visibly inert while a stroke resolves. The engine is the authority (`__pgStrokeResolving`); this
  // only mirrors it onto the button, on the animation frame, so the disabled state can never lag the ball.
  const sync = () => {
    if (window.__pgStrokeResolving) btn.disabled = !!window.__pgStrokeResolving();
    requestAnimationFrame(sync);
  };
  requestAnimationFrame(sync);
}

